import { Component, Ref } from "@eastsideco/salvo-ts";
import { cart } from "@/theme/Cart";
import { ThemeComponent } from "@/theme/ThemeComponent";

/**
 * Product Details
 */
@Component
export class ProductAddons extends ThemeComponent {
    @Ref({required: false}) addAddOn!: HTMLButtonElement[];
    @Ref({required: false}) addonOptionDropdown!: HTMLSelectElement[];
    @Ref({required: false}) addonCustomDropdown!: HTMLElement[];
    @Ref({required: false}) dropdownButton!: HTMLButtonElement[];
    @Ref({required: false}) dropdownOption!: HTMLButtonElement[];

    init() {
        // Add event listeners for single item submit buttons
        document.addEventListener('click', (e) => {
            const button = (e.target as HTMLElement).closest('[data-ref="addAddOn"]');
            if (!button) {return;}
            e.preventDefault();

            const id = button.getAttribute('data-productId');
            if (id) {
                void this.addProductToCart(Number(id));
            }
        });


        this.changeVariant();
        this.initDropdownListeners();
        this.initCustomDropdowns();
    }

    changeVariant() {
        const addOns = document.querySelectorAll<HTMLElement>('.add-on-product__swatch');
        for (const addOn of addOns) {
            this.$listen(addOn, 'click', (event) => {
                event.preventDefault();
                const id = addOn.getAttribute('id');
                if (id) {
                    // Find the parent add-on product container
                    let container: HTMLElement | null = addOn.parentElement;
                    while (container && !container.classList.contains('add-on-product')) {
                        container = container.parentElement;
                    }
                    if (!container) {return;}

                    // Remove 'is-selected' class from all swatches in this product
                    const allSwatches = container.querySelectorAll<HTMLElement>('.add-on-product__swatch');
                    for (const swatch of allSwatches) {
                        swatch.classList.remove('is-selected');
                    }

                    // Add 'is-selected' class to the clicked swatch
                    addOn.classList.add('is-selected');

                    // Update the hidden input id in the form
                    const hiddenInput = container.querySelector<HTMLInputElement>(
                        '.add-on-product__button input[name="id"]'
                    );
                    if (hiddenInput) {
                        hiddenInput.value = id;
                    }

                    // Update the button's data-productId attribute
                    const submitButton = container.querySelector<HTMLButtonElement>(
                        '.add-on-product__button [data-ref="addAddOn"]'
                    );
                    if (submitButton) {
                        submitButton.setAttribute('data-productId', id);
                    }

                    this.updateImage(container, addOn.getAttribute('data-variant-image') || '');
                    this.updateOptions(container, addOn);
                    this.updatePrice(container, addOn);

                }
            });
        }
    }

    initDropdownListeners() {
        // Add event listeners for dropdown option changes
        if (this.addonOptionDropdown && this.addonOptionDropdown.length > 0) {
            for (const dropdown of this.addonOptionDropdown) {
                this.$listen(dropdown, 'change', (event) => {
                    event.preventDefault();
                    this.handleDropdownChange(dropdown);
                });
            }
        }
    }

    handleDropdownChange(dropdown: HTMLSelectElement) {
        const selectedOption = dropdown.options[dropdown.selectedIndex];
        if (!selectedOption) {return;}

        // Skip if this is a placeholder option (empty value)
        if (!selectedOption.value) {return;}

        // Find the parent add-on product container
        let container: HTMLElement | null = dropdown.parentElement;
        while (container && !container.classList.contains('add-on-product')) {
            container = container.parentElement;
        }
        if (!container) {return;}

        // Check if this is a size option dropdown by looking at the parent dropdown element
        let dropdownContainer: HTMLElement | null = dropdown.parentElement;
        while (dropdownContainer && !dropdownContainer.classList.contains('add-on-product__dropdown')) {
            dropdownContainer = dropdownContainer.parentElement;
        }
        const isSizeOption = dropdownContainer?.getAttribute('data-is-size-option') === 'true';

        // Get variant data from the selected option
        const variantId = selectedOption.getAttribute('data-variant-id');
        const variantImage = selectedOption.getAttribute('data-variant-image');
        const variantPrice = selectedOption.getAttribute('data-variant-price');
        const variantComparePrice = selectedOption.getAttribute('data-variant-compare-price');
        const variantAvailable = selectedOption.getAttribute('data-variant-available');
        const variantOptions = selectedOption.getAttribute('data-variant-options');
        const variantSize = selectedOption.getAttribute('data-variant-size');

        if (variantId) {
            // Update the hidden input id in the form
            const hiddenInput = container.querySelector<HTMLInputElement>(
                '.add-on-product__button input[name="id"]'
            );
            if (hiddenInput) {
                hiddenInput.value = variantId;
            }

            // Update the button's data-productId attribute and enable if it was disabled
            const submitButton = container.querySelector<HTMLButtonElement>(
                '.add-on-product__button [data-ref="addAddOn"]'
            );
            if (submitButton) {
                submitButton.setAttribute('data-productId', variantId);

                // If this was a size selection, enable the button
                if (isSizeOption) {
                    submitButton.disabled = false;
                    submitButton.classList.remove('is-disabled');
                }
            }

            // Update image, options, and price
            if (variantImage) {
                this.updateImage(container, variantImage);
            }

            // Create a mock swatch element to reuse existing update methods
            const mockSwatch = document.createElement('div');
            mockSwatch.setAttribute('data-variant-options', variantOptions || '');
            mockSwatch.setAttribute('data-variant-size', variantSize || '');
            mockSwatch.setAttribute('data-variant-price', variantPrice || '');
            mockSwatch.setAttribute('data-variant-compare-price', variantComparePrice || '');
            mockSwatch.setAttribute('data-variant-available', variantAvailable || 'true');

            this.updateOptions(container, mockSwatch);
            this.updatePrice(container, mockSwatch);
        }
    }

    initCustomDropdowns() {
        // Initialize custom dropdown functionality
        if (this.dropdownButton && this.dropdownButton.length > 0) {
            for (const button of this.dropdownButton) {
                this.$listen(button, 'click', (event) => {
                    event.preventDefault();
                    this.toggleDropdown(button);
                });
            }
        }

        if (this.dropdownOption && this.dropdownOption.length > 0) {
            for (const option of this.dropdownOption) {
                this.$listen(option, 'click', (event) => {
                    event.preventDefault();
                    this.selectDropdownOption(option);
                });
            }
        }

        // Close dropdowns when clicking outside
        document.addEventListener('click', (event) => {
            this.closeAllDropdowns(event);
        });
    }

    toggleDropdown(button: HTMLButtonElement) {
        // Find the dropdown container
        let dropdown: HTMLElement | null = button.parentElement;
        while (dropdown && !dropdown.classList.contains('add-on-product__dropdown')) {
            dropdown = dropdown.parentElement;
        }
        if (!dropdown) {return;}

        const isOpen = dropdown.classList.contains('is-open');

        // Close all other dropdowns first
        this.closeAllDropdowns();

        // Toggle current dropdown
        if (!isOpen) {
            dropdown.classList.add('is-open');
        }
    }

    selectDropdownOption(option: HTMLButtonElement) {
        // Find the dropdown container
        let dropdown: HTMLElement | null = option.parentElement;
        while (dropdown && !dropdown.classList.contains('add-on-product__dropdown')) {
            dropdown = dropdown.parentElement;
        }
        if (!dropdown) {return;}

        const value = option.getAttribute('data-value');
        if (!value) {return;}

        // Update the dropdown button text
        const dropdownText = dropdown.querySelector<HTMLElement>('[data-ref="dropdownText"]');
        if (dropdownText) {
            dropdownText.textContent = value;
        }

        // Update hidden select
        const hiddenSelect = dropdown.querySelector<HTMLSelectElement>('[data-ref="hiddenSelect"]');
        if (hiddenSelect) {
            hiddenSelect.value = value;
        }

        // Update selected state
        const allOptions = dropdown.querySelectorAll<HTMLButtonElement>('[data-ref="dropdownOption"]');
        for (const opt of allOptions) {
            opt.classList.remove('is-selected');
        }
        option.classList.add('is-selected');

        // Close dropdown
        dropdown.classList.remove('is-open');

        // Handle variant change
        this.handleCustomDropdownChange(option, dropdown);
    }

    handleCustomDropdownChange(option: HTMLButtonElement, dropdown: HTMLElement) {
        // Find the parent add-on product container
        let container: HTMLElement | null = dropdown.parentElement;
        while (container && !container.classList.contains('add-on-product')) {
            container = container.parentElement;
        }
        if (!container) {return;}

        // Check if this is a size option dropdown
        const isSizeOption = dropdown.getAttribute('data-is-size-option') === 'true';

        // Get variant data from the selected option
        const variantId = option.getAttribute('data-variant-id');
        const variantImage = option.getAttribute('data-variant-image');
        const variantPrice = option.getAttribute('data-variant-price');
        const variantComparePrice = option.getAttribute('data-variant-compare-price');
        const variantAvailable = option.getAttribute('data-variant-available');
        const variantOptions = option.getAttribute('data-variant-options');
        const variantSize = option.getAttribute('data-variant-size');

        if (variantId) {
            // Update the hidden input id in the form
            const hiddenInput = container.querySelector<HTMLInputElement>(
                '.add-on-product__button input[name="id"]'
            );
            if (hiddenInput) {
                hiddenInput.value = variantId;
            }

            // Update the button's data-productId attribute and enable if it was disabled
            const submitButton = container.querySelector<HTMLButtonElement>(
                '.add-on-product__button [data-ref="addAddOn"]'
            );
            if (submitButton) {
                submitButton.setAttribute('data-productId', variantId);

                // If this was a size selection, enable the button
                if (isSizeOption) {
                    submitButton.disabled = false;
                    submitButton.classList.remove('is-disabled');
                }
            }

            // Update image, options, and price
            if (variantImage) {
                this.updateImage(container, variantImage);
            }

            // Create a mock swatch element to reuse existing update methods
            const mockSwatch = document.createElement('div');
            mockSwatch.setAttribute('data-variant-options', variantOptions || '');
            mockSwatch.setAttribute('data-variant-size', variantSize || '');
            mockSwatch.setAttribute('data-variant-price', variantPrice || '');
            mockSwatch.setAttribute('data-variant-compare-price', variantComparePrice || '');
            mockSwatch.setAttribute('data-variant-available', variantAvailable || 'true');

            this.updateOptions(container, mockSwatch);
            this.updatePrice(container, mockSwatch);
        }
    }

    closeAllDropdowns(event?: Event) {
        const allDropdowns = document.querySelectorAll<HTMLElement>('.add-on-product__dropdown.is-open');
        for (const dropdown of allDropdowns) {
            // If event is provided, check if click is inside dropdown
            if (event && dropdown.contains(event.target as Node)) {
                continue;
            }
            dropdown.classList.remove('is-open');
        }
    }

    updateImage(container: HTMLElement, variantImageUrl: string) {
        // Update the display_variant image when swatch is clicked
        if (variantImageUrl && variantImageUrl !== 'null' && variantImageUrl.trim() !== '') {
            let productImage = container.querySelector<HTMLImageElement>('.add-on-product__image img');
            if (!productImage) {
                productImage = container.querySelector<HTMLImageElement>('.add-on-product__image.object-cover img');
            }
            if (!productImage) {
                productImage = container.querySelector<HTMLImageElement>('img');
            }

            if (productImage) {
                // Store original attributes for fallback
                const originalSrc = productImage.src;
                productImage.src = variantImageUrl;
                if (productImage.srcset) {
                    productImage.srcset = variantImageUrl;
                }

                // Handle image load error
                productImage.onerror = () => {
                    if (productImage) {
                        productImage.src = originalSrc;
                    }
                };
            }
        }
    }

    updateOptions(container: HTMLElement, swatchElement: HTMLElement) {
        // Update the product options when swatch is clicked
        const variantOptions = swatchElement.getAttribute('data-variant-options');
        const variantSize = swatchElement.getAttribute('data-variant-size');
        const optionsList = container.querySelector<HTMLUListElement>('.product-card__options');

        // Only update if the options list exists (not hidden due to other variant types)
        if (optionsList) {
            // Clear existing options first
            optionsList.innerHTML = '';

            if (variantOptions && variantOptions.trim() !== '' && variantOptions !== 'null') {
                // Add variant options
                const options = variantOptions
                    .split('|')
                    .filter((option) => option && option.trim() !== '' && option !== 'Default Title');

                for (const option of options) {
                    const li = document.createElement('li');
                    li.className = 'product-card__option';
                    li.textContent = option.trim();
                    optionsList.appendChild(li);
                }
            }

            // Add variant size if available
            if (variantSize && variantSize.trim() !== '' && variantSize !== 'null') {
                const li = document.createElement('li');
                li.className = 'product-card__option';
                li.textContent = variantSize.trim();
                optionsList.appendChild(li);
            }

            // If no options were added, show a fallback
            if (optionsList.children.length === 0) {
                const li = document.createElement('li');
                li.className = 'product-card__option';
                li.textContent = 'No options available';
                optionsList.appendChild(li);
            }
        }
    }

    updatePrice(container: HTMLElement, swatchElement: HTMLElement) {
        // Update the product price when swatch is clicked
        const variantPrice = swatchElement.getAttribute('data-variant-price');
        const variantComparePrice = swatchElement.getAttribute('data-variant-compare-price');
        const variantAvailable = swatchElement.getAttribute('data-variant-available');

        const priceContainer = container.querySelector<HTMLElement>('.add-on-product__price');

        if (priceContainer) {
            // Find price elements within the price container - try multiple selectors
            let currentPriceElement = priceContainer.querySelector<HTMLElement>('.price__current');
            if (!currentPriceElement) {
                currentPriceElement = priceContainer.querySelector<HTMLElement>('.price');
            }
            if (!currentPriceElement) {
                currentPriceElement = priceContainer.querySelector<HTMLElement>(
                    '[class*="price"]:not([class*="compare"])'
                );
            }
            if (!currentPriceElement) {
                // Try to find any element with price text content
                const allElements = priceContainer.querySelectorAll<HTMLElement>('*');
                for (const el of allElements) {
                    if (el.textContent && el.textContent.includes('$')) {
                        currentPriceElement = el;
                        break;
                    }
                }
            }

            const comparePriceElement = priceContainer.querySelector<HTMLElement>(
                '.price__compare, .price--compare, [class*="compare"]'
            );

            // Update current price
            if (currentPriceElement && variantPrice) {
                currentPriceElement.textContent = `$${variantPrice}`;
            }

            // Update compare price if it exists
            if (comparePriceElement && variantComparePrice && variantComparePrice.trim() !== '') {
                comparePriceElement.textContent = `$${variantComparePrice}`;
                comparePriceElement.style.display = 'inline';
            } else if (comparePriceElement) {
                comparePriceElement.style.display = 'none';
            }

            // Update availability classes if needed
            if (variantAvailable === 'false') {
                priceContainer.classList.add('price--unavailable');
            } else {
                priceContainer.classList.remove('price--unavailable');
            }
        }
    }

    protected async addProductToCart(ids: number) {
        try {
            const idsToAdd: { id: number; quantity: number }[] = [];
            const cartCheck: { id: number; inCart: boolean }[] = [];
            const idUse = ids; // ids is already a number, no need to parse
            const itemInCart: boolean = cart.checkCart(idUse);
            const addString = { id: idUse, quantity: 1 };
            const checkString = { id: idUse, inCart: itemInCart };
            cartCheck.push(checkString);
            idsToAdd.push(addString);

            await cart.addItems(idsToAdd)
                .then(() => {
                    document.dispatchEvent(new Event('cartChanged'));
                })
                .catch((error: unknown) => {
                    this.$log.error('Cart error', { error });
                });
        } catch (error) {
            this.$log.error('Unexpected cart error', { error });
        }
    }

}
